import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { MiddleClassCodeComboBoxModel } from './middle-class-code-combo-box.model';
import { GetMiddleClassListService } from './api-services/get-middle-class-list.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 中分類コードコンボボックスを制御するクラス
 *
 * @export MiddleClassCodeComboBoxComponent
 * @class MiddleClassCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-middle-class-code-combo-box',
  templateUrl: 'middle-class-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MiddleClassCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetMiddleClassListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetMiddleClassListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})

export class MiddleClassCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  // 受注コード
  public selectedOrderCode: string = <string>'';
  // 部門コード
  public selectedClassCode: string = <string>'';
  // 大分類コード
  public selectedLargeClassCode: string = <string>'';
  // 中分類コード
  public selectedMiddleClassCode: string = <string>'';
  // 中分類名
  public selectedMiddleClassName: string = <string>'';
  // マネージャ
  public model: MiddleClassCodeComboBoxModel = new MiddleClassCodeComboBoxModel();

  public selectedIndex: number = <number>-1;

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;
  @Input() required: boolean = <boolean>false;
  @Input() orderCode: string = <string>'';
  @Input() classCode: string = <string>'';
  @Input() largeClassCode: string = <string>'';
  @Input() disabled: boolean = <boolean>false;
  @Input() isInitOrderCode: string = <string>'';
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() left: number = <number>10;
  @Input() top: number = <number>10;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() public initFocusItem: any = null;                                    // ロード完了後フォーカスを移動させるid
  @Input() public tabindex: number = null;
  @Input() classCodeComboBoxFlg: boolean = <boolean>false;
  @Input() largeClassCodeComboBoxFlg: boolean = <boolean>false;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() forcedEnabledMode: string = null;

  /**
   * Creates an instance of MiddleClassCodeComboBoxComponent.
   * @param {ElementRef} elementRef
   * @param {GetMiddleClassListService} getMiddleClassListService
   * @memberof MiddleClassCodeComboBoxComponent
   */
  constructor(
    public elementRef: ElementRef,
    private getMiddleClassListService: GetMiddleClassListService,
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedMiddleClassCode) {
      this.selectedMiddleClassCode = text;
      this.setMiddleClassCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof MiddleClassCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof MiddleClassCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (!this.classCodeComboBoxFlg) {
      this.reload();
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof MiddleClassCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
        case 'classCode':
          this.selectedIndex = -1;
          if (this.classCodeComboBoxFlg && !this.largeClassCodeComboBoxFlg) {
            this.classCode = changes.classCode.currentValue;
            if (!PlessViewUtilsSub.isEmpty(this.classCode)) {
              this.getData();
            } else {
              this.clear();
            }
          } else {
            if (changes.largeClassCode !== undefined) {
              this.largeClassCode = changes.largeClassCode.currentValue;
              if (!PlessViewUtilsSub.isEmpty(this.largeClassCode)) {
                this.getData();
              } else {
                this.clear();
              }
            }
          }
          break;
        case 'largeClassCode':
          this.selectedIndex = -1;
          this.largeClassCode = changes.largeClassCode.currentValue;
          if (!PlessViewUtilsSub.isEmpty(this.largeClassCode)) {
            this.getData();
          } else {
            this.clear();
          }
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof MiddleClassCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * 条件パラメータの設定
   *
   * @param {string} orderCode
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public setConditionParams(orderCode: string): void {
    this.orderCode = orderCode;
    // プロパティ初期化フラグを設定
    this.model.isInitOrderCode = true;
    // データ取得
    this.getMiddleClassListService.execute(this.model, this.orderCode, this.classCode, this.largeClassCode);
  }

  /**
   * コンボボックスの選択中分類コードを切り替える
   *
   * @param {string} middleClassCode 中分類コード
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public setMiddleClassCode(middleClassCode: string): void {
    this.model.setMiddleClassCode = middleClassCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedMiddleClassCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public updateSelectedMiddleClassCode(): void {
    const orderList: Array<any> = this.model.middleClassList;
    for (let i = Number(0); i < orderList.length; i++) {
      if (PlessViewUtilsSub.trim(orderList[i].MiddleClassCode) === this.model.setMiddleClassCode) {
        this.selectedIndex = i;
        this.update(null);
      }
    }
    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

  /**
   * 更新
   *
   * @param {*} [e=null]
   * @returns {void}
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public update(e: any = null): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedClassCode = <string>'';
      this.selectedLargeClassCode = <string>'';
      this.selectedMiddleClassCode = <string>'';
      this.selectedMiddleClassName = <string>'';
      return;
    }

    // バインドフィールド設定
    this.selectedClassCode = this.model.middleClassList[this.selectedIndex].ClassCode;
    this.selectedLargeClassCode = this.model.middleClassList[this.selectedIndex].LargeClassCode;
    this.selectedMiddleClassCode = this.model.middleClassList[this.selectedIndex].MiddleClassCode;
    this.selectedMiddleClassName = this.model.middleClassList[this.selectedIndex].MiddleClassName;
    this.model.setMiddleClassCode = <string>'';
  }

  /**
   * 再ロード
   *
   * @param {string} [classCode=''] 部門コード
   * @param {string} [largeClassCode=''] 大分類コード
   * @returns {void}
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public reload(classCode = '', largeClassCode = ''): void {
    if ((this.classCodeComboBoxFlg && PlessViewUtilsSub.isEmpty(classCode)) ||
      (this.largeClassCodeComboBoxFlg && PlessViewUtilsSub.isEmpty(largeClassCode))) {
      this.clear();
      return;
    }

    // データ取得
    this.getMiddleClassListService.execute(this.model, this.orderCode, this.classCode, this.largeClassCode);

    // バインドフィールド初期化
    this.selectedClassCode = <string>'';
    this.selectedLargeClassCode = <string>'';
    this.selectedMiddleClassCode = <string>'';
    this.selectedMiddleClassName = <string>'';
  }

  /**
   * クリア
   *
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public clear(): void {
    this.model = new MiddleClassCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;

    this.selectedOrderCode = <string>'';
    this.selectedClassCode = <string>'';
    this.selectedLargeClassCode = <string>'';
    this.selectedMiddleClassCode = <string>'';
    this.selectedMiddleClassName = <string>'';
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.setSelectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.setSelectedIndex >= 0) {
      this.selectedIndex = this.model.setSelectedIndex;
      this.model.setSelectedIndex = -1;
      this.update(null);
    }
  }

  /**
   * データ取得
   *
   * @memberof MiddleClassCodeComboBoxComponent
   */
  public getData(): void {
    this.getMiddleClassListService.execute(this.model, this.orderCode, this.classCode, this.largeClassCode);
  }

}
